import { ref, type Ref, shallowRef, watch } from "vue";
import * as Cesium from "cesium";
import { BACKEND_BASE_URL, backendFetchParams } from "@/utils/backendFetch";

export function useJakartoPointclouds(
  viewer: Ref<Cesium.Viewer | null>,
  pointSize: Ref<number>,
  aerial: boolean
) {
  const jakartoPointclouds = shallowRef<Cesium.Cesium3DTileset | null>(null);
  const showJakartoPointclouds = ref(false);

  // Add maps.jakarto.com to trusted servers for authentication
  Cesium.TrustedServers.add("maps.jakarto.com", 443);

  const loadJakartoPointclouds = async (): Promise<void> => {
    const endpoint = `${BACKEND_BASE_URL}/backend/3dtiles/pointclouds`;
    if (!viewer.value) return;

    try {
      const resource = new Cesium.Resource({
        url: endpoint,
        headers: backendFetchParams().headers,
        queryParameters: { aerial: aerial },
      });
      const tileset = await Cesium.Cesium3DTileset.fromUrl(resource, {
        maximumScreenSpaceError: aerial ? 16: 32,
        cacheBytes: 2 * 1024 * 1024 * 1024, // 2GB,
      });

      tileset.style = new Cesium.Cesium3DTileStyle({
        pointSize: pointSize.value,
      });

      watch(pointSize, (size) => {
        if (!tileset) return;

        tileset.style = new Cesium.Cesium3DTileStyle({
          pointSize: size,
        });
        viewer.value?.scene.requestRender();
      });

      console.log("Jakarto pointclouds loaded successfully");

      viewer.value!.scene.primitives.add(tileset);
      jakartoPointclouds.value = tileset;
    } catch (error) {
      console.error("Error loading Jakarto pointclouds:", error);
    }
  };

  const toggleJakartoPointclouds = async (): Promise<void> => {
    if (!viewer.value) return;
    showJakartoPointclouds.value = !showJakartoPointclouds.value;

    if (showJakartoPointclouds.value) {
      await loadJakartoPointclouds();
    } else {
      // Remove Jakarto pointclouds
      if (jakartoPointclouds.value) {
        viewer.value!.scene.primitives.remove(jakartoPointclouds.value);
        jakartoPointclouds.value = null;
      }
    }
    viewer.value.scene.requestRender();
  };

  return {
    jakartoPointclouds,
    showJakartoPointclouds,
    toggleJakartoPointclouds,
    loadJakartoPointclouds,
  };
}
